import React, { useState, useEffect } from 'react';
import { getAllUsers } from '../api/userApi';
import { format } from 'date-fns';
import logger from '../utils/logger';


import axios from 'axios';
import apiUrl from '../config';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showPasswordForm, setShowPasswordForm] = useState({});
  const [newPassword, setNewPassword] = useState({});
  const [pwChangeStatus, setPwChangeStatus] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const userData = await getAllUsers();
      setUsers(userData);
    } catch (error) {
      logger.error('Error fetching users:', error);
      setError(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for new sort field
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let comparison = 0;
    
    if (sortField === 'name' || sortField === 'email') {
      comparison = a[sortField]?.localeCompare(b[sortField] || '') || 0;
    } else if (sortField === 'createdAt') {
      comparison = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    } else if (['loginCount', 'dailyJournals', 'weeklyJournals', 'performanceJournals', 
               'breathCycles', 'meditationMinutes', 'concentrationGames'].includes(sortField)) {
      // Handle numeric stats fields
      comparison = (a[sortField] || 0) - (b[sortField] || 0);
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="p-4 bg-white rounded shadow">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-white rounded shadow">
        <div className="text-red-500 text-center">
          <p>{error}</p>
          <button 
            onClick={fetchUsers}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800">Registered Users</h2>
        <p className="text-sm text-gray-600">Total: {users.length} users</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('name')}
              >
                Name {getSortIcon('name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('email')}
              >
                Email {getSortIcon('email')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                Registration Date {getSortIcon('createdAt')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('loginCount')}
              >
                Logins {getSortIcon('loginCount')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('dailyJournals')}
              >
                Daily Journals {getSortIcon('dailyJournals')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('weeklyJournals')}
              >
                Weekly Journals {getSortIcon('weeklyJournals')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('performanceJournals')}
              >
                Performance Journals {getSortIcon('performanceJournals')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('breathCycles')}
              >
                Breath Cycles {getSortIcon('breathCycles')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('meditationMinutes')}
              >
                Meditation Minutes {getSortIcon('meditationMinutes')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('concentrationGames')}
              >
                Concentration Games {getSortIcon('concentrationGames')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedUsers.length > 0 ? (
              sortedUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{formatDate(user.createdAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.loginCount || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.dailyJournals || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.weeklyJournals || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.performanceJournals || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.breathCycles || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.meditationMinutes || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.concentrationGames || 0}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:underline text-xs"
                      onClick={() => handleShowPasswordForm(user._id)}
                    >
                      Change Password
                    </button>
                    {showPasswordForm[user._id] && (
                      <div className="mt-2 flex flex-col space-y-2">
                        <input
                          type="password"
                          placeholder="New password"
                          value={newPassword[user._id] || ''}
                          onChange={e => handlePasswordInput(user._id, e.target.value)}
                          className="border px-2 py-1 rounded text-xs"
                        />
                        <button
                          className="bg-green-500 text-white text-xs rounded px-2 py-1 hover:bg-green-600"
                          onClick={() => handlePasswordChange(user._id)}
                          disabled={!newPassword[user._id] || newPassword[user._id].length < 6}
                        >
                          Save
                        </button>
                        {pwChangeStatus[user._id] && (
                          <div className="text-xs mt-1 text-center text-gray-600">{pwChangeStatus[user._id]}</div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="px-6 py-4 text-center text-sm text-gray-500">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  function handleShowPasswordForm(userId) {
    setShowPasswordForm((prev) => ({ ...prev, [userId]: !prev[userId] }));
    setPwChangeStatus((prev) => ({ ...prev, [userId]: '' }));
  }

  function handlePasswordInput(userId, value) {
    setNewPassword((prev) => ({ ...prev, [userId]: value }));
  }

  async function handlePasswordChange(userId) {
    setPwChangeStatus((prev) => ({ ...prev, [userId]: 'Loading...' }));
    try {
      const response = await axios.patch(`${apiUrl}/api/users/${userId}/admin-reset-password`, { password: newPassword[userId] }, { withCredentials: true });
      setPwChangeStatus((prev) => ({ ...prev, [userId]: response.data.message || 'Success!' }));
      setNewPassword((prev) => ({ ...prev, [userId]: '' }));
      setTimeout(() => setShowPasswordForm((prev) => ({ ...prev, [userId]: false })), 2000);
    } catch (error) {
      let msg = 'Error';
      if (error.response && error.response.data && error.response.data.message) {
        msg = error.response.data.message;
      } else if (error.message) {
        msg = error.message;
      }
      setPwChangeStatus((prev) => ({ ...prev, [userId]: msg }));
    }
  }

  // ...rest of the component
}

export default UserList;
